/**
 * SurveyCard + SurveyFloatingCard
 *
 * Self-hosted in-app micro-survey (thumbs / rating / nps / choice / text +
 * optional free-text follow-up), remote-configured via `useSurvey`.
 *
 * Two presentations from one core:
 *   - 'floating' (desktop): a fixed bottom-right card, mirroring
 *     DesktopProFloatingCard's slot. On /home it sits "in place of" the upgrade
 *     card (the Pro card is suppressed while a survey shows — see _layout.tsx).
 *   - 'inline' (mobile): rendered in the screen flow, below the header.
 *
 * `SurveyFloatingCard` (default export) is the global, DESKTOP-ONLY wrapper
 * mounted in `app/_layout.tsx`. The inline mobile variant is mounted by the
 * feed / listing-detail screens via the named `SurveyCard` export. Exactly one
 * of them runs `useSurvey` for a given viewport, so there's no double fetch.
 *
 * Flow: pick answer -> optional message -> submit -> thank-you (auto-hide).
 * The "×" dismisses (snoozes for the survey's frequency_days).
 */

import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { usePathname } from 'expo-router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import { useResponsive } from '../../hooks/useResponsive';
import { useSurvey } from '../../hooks/useSurvey';
import { setHomeSurveyActive, useListingTourActive } from './surveyVisibility';
import SurveyQuestionInput from './SurveyQuestionInput';

const THANKS_AUTOHIDE_MS = 2600;
// Delay before the floating card first appears, per surface. /home waits so the
// survey doesn't interrupt the initial feed render; listing detail is immediate.
// Inline placement shows immediately (it's part of the page, not an overlay).
const SHOW_DELAY_MS = { home: 8000, listing_detail: 0 };
const EMPTY_CONTEXT = {};

const surfaceForPath = (pathname) => {
  if (pathname === '/home') return 'home';
  if (pathname && pathname.startsWith('/listing/')) return 'listing_detail';
  return null;
};

/**
 * Core survey card. Renders the active survey for `surface`, scoped by
 * `context` (e.g. { listing_id }). `presentation` is 'floating' or 'inline'.
 */
export const SurveyCard = ({ surface, context = EMPTY_CONTEXT, presentation = 'floating' }) => {
  const { survey, eligible, loading, submit, dismiss, markShown } = useSurvey(surface, {
    context,
  });

  const [phase, setPhase] = useState('ask'); // 'ask' | 'comment' | 'thanks'
  const [answerValue, setAnswerValue] = useState(null); // type-specific answer
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [delayElapsed, setDelayElapsed] = useState(false); // surface show-delay gate

  const isInline = presentation === 'inline';
  const listingId = context?.listing_id || null;

  // Instance identity = survey + listing scope. The listing survey is ONE row
  // reused across listings, so survey.id alone is constant A->B; key per-listing
  // state off the listing too, or navigating between listings won't reset the
  // card or re-fire the impression.
  const instanceId = survey
    ? `${survey.id}::${surface === 'listing_detail' ? listingId || '' : ''}`
    : null;

  // Reset local UI when the resolved instance changes (new surface or listing).
  useEffect(() => {
    setPhase('ask');
    setAnswerValue(null);
    setMessage('');
    setHidden(false);
  }, [instanceId]);

  // Show delay. Anchored to instance identity, NOT eligibility, so submitting
  // (which flips eligible -> false) doesn't reset the gate and hide thank-you.
  useEffect(() => {
    setDelayElapsed(false);
    if (!survey) return undefined;
    const delay = isInline ? 0 : SHOW_DELAY_MS[surface] ?? 0;
    if (delay <= 0) {
      setDelayElapsed(true);
      return undefined;
    }
    const t = setTimeout(() => setDelayElapsed(true), delay);
    return () => clearTimeout(t);
  }, [instanceId, surface, isInline]);

  // Fire impression once the survey is eligible AND past its show delay.
  const shownRef = useRef(false);
  useEffect(() => {
    shownRef.current = false;
  }, [instanceId]);
  useEffect(() => {
    if (shownRef.current || !survey || !eligible || !delayElapsed) return;
    shownRef.current = true;
    markShown();
  }, [survey, eligible, delayElapsed, markShown]);

  // Auto-hide after the thank-you state.
  useEffect(() => {
    if (phase !== 'thanks') return undefined;
    const t = setTimeout(() => setHidden(true), THANKS_AUTOHIDE_MS);
    return () => clearTimeout(t);
  }, [phase]);

  // Floating /home only: once a survey is eligible, suppress the Pro card across
  // the sequence (8s blank delay -> survey -> thank-you), so the page shows
  // nothing, then the survey, then the upgrade card. Not gated on `loading`, so
  // loads with no eligible survey never hide the Pro card.
  const suppressHomePro =
    presentation === 'floating' &&
    surface === 'home' &&
    !hidden &&
    !!survey &&
    (eligible || phase === 'thanks');
  useEffect(() => {
    setHomeSurveyActive(suppressHomePro);
    return () => setHomeSurveyActive(false);
  }, [suppressHomePro]);

  // Free-text follow-up. Desktop (floating) offers it after any answer. Mobile
  // (inline) keeps the happy path one-tap, but DOES offer the box after a thumbs
  // down-vote so unhappy users can explain (mobile is where most down-votes come
  // from, and they were previously silent).
  const messageEnabled = survey?.allow_message !== false;
  const isTextType = survey?.type === 'text';
  const shouldComment = useCallback(
    (value) =>
      messageEnabled &&
      (!isInline || (survey?.type === 'thumbs' && value === 'down')),
    [messageEnabled, isInline, survey?.type]
  );

  // Send to the backend, then show thanks regardless — a failed insert still
  // shouldn't nag the user now; the local throttle keeps it from reappearing.
  const doSubmit = useCallback(
    async (value, msg) => {
      if (submitting) return;
      setSubmitting(true);
      await submit({ value, message: msg });
      setSubmitting(false);
      setPhase('thanks');
    },
    [submitting, submit]
  );

  // User picked an answer (thumbs/rating/nps/choice). If a free-text follow-up
  // is enabled, advance to the comment step; otherwise submit immediately.
  const handlePick = useCallback(
    (value) => {
      setAnswerValue(value);
      if (shouldComment(value)) {
        setPhase('comment');
      } else {
        doSubmit(value, '');
      }
    },
    [shouldComment, doSubmit]
  );

  // Send from the comment step, or from a text-type question (message is the answer).
  const handleSend = useCallback(() => {
    if (submitting) return;
    if (isTextType) {
      if (!message.trim()) return; // text answer is required
      doSubmit(null, message);
    } else {
      doSubmit(answerValue, message);
    }
  }, [submitting, isTextType, message, answerValue, doSubmit]);

  const handleDismiss = useCallback(() => {
    setHidden(true);
    dismiss();
  }, [dismiss]);

  // ---- Gating ----
  if (!surface || hidden || loading || !survey) return null;
  // Free-text entry is removed on mobile, so a text-only survey can't be
  // answered inline — skip it there.
  if (isInline && isTextType) return null;
  // Honour the show-delay for the initial appearance; once shown this mount
  // (shownRef) keep it visible so a re-resolve can't yank it mid-view; the
  // thank-you state (post-submit, eligible already false) is also exempt.
  const visible = shownRef.current || (eligible && delayElapsed) || phase === 'thanks';
  if (!visible) return null;

  const panel = (
    <View style={styles.card}>
      {Platform.OS !== 'web' && (
        <LinearGradient
          colors={['rgba(20, 76, 184, 0.18)', 'rgba(20, 76, 184, 0.06)']}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={StyleSheet.absoluteFill}
          pointerEvents="none"
        />
      )}

      {phase !== 'thanks' && (
        <Pressable
          onPress={handleDismiss}
          style={styles.closeBtn}
          accessibilityRole="button"
          accessibilityLabel="Dismiss survey"
          hitSlop={8}
        >
          <Text style={styles.closeBtnText}>×</Text>
        </Pressable>
      )}

      {phase === 'thanks' ? (
        <View style={styles.thanksWrap}>
          <MaterialCommunityIcons name="check-circle" size={22} color="#3AE869" />
          <Text style={styles.thanksText}>Thanks for the feedback!</Text>
        </View>
      ) : (
        <>
          <View style={styles.head}>
            <MaterialCommunityIcons name="message-text-outline" size={12} color="#8FB3FF" />
            <Text style={styles.headText}>Quick question</Text>
          </View>
          <Text style={styles.title}>{survey.question}</Text>

          {phase === 'ask' && !isTextType && (
            <SurveyQuestionInput
              type={survey.type}
              config={survey.config}
              value={answerValue}
              onChange={handlePick}
              disabled={submitting}
            />
          )}

          {!isInline && (phase === 'comment' || isTextType) && (
            <>
              {phase === 'comment' &&
                (survey.type === 'thumbs' ? (
                  <View style={styles.chosenRow}>
                    <MaterialCommunityIcons
                      name={answerValue === 'up' ? 'thumb-up' : 'thumb-down'}
                      size={16}
                      color={answerValue === 'up' ? '#3AE869' : '#FF7B00'}
                    />
                    <Text style={styles.chosenText}>
                      {answerValue === 'up' ? 'Glad it helps.' : 'Thanks, noted.'}
                    </Text>
                  </View>
                ) : (
                  <View style={styles.chosenRow}>
                    <MaterialCommunityIcons name="check-circle" size={16} color="#3AE869" />
                    <Text style={styles.chosenText}>Got it. Anything to add?</Text>
                  </View>
                ))}
              <TextInput
                style={styles.input}
                placeholder={survey.message_placeholder}
                placeholderTextColor="rgba(255,255,255,0.4)"
                value={message}
                onChangeText={setMessage}
                multiline
                numberOfLines={3}
                maxLength={500}
                editable={!submitting}
              />
              <Pressable
                onPress={handleSend}
                disabled={submitting || (isTextType && !message.trim())}
                style={[
                  styles.ctaWrapper,
                  (submitting || (isTextType && !message.trim())) && styles.ctaDisabled,
                ]}
                accessibilityRole="button"
                accessibilityLabel="Send feedback"
              >
                <LinearGradient
                  colors={['#538AF9', '#2466ED']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.cta}
                >
                  <Text style={styles.ctaText}>{submitting ? 'Sending…' : 'Send'}</Text>
                </LinearGradient>
              </Pressable>
            </>
          )}
        </>
      )}
    </View>
  );

  // Inline (mobile): a minimalist, borderless single-line bar — question on the
  // left, answer control on the right. No frame/background; "fixed like the
  // header" comes from where the screen mounts it (outside the scroll view).
  if (isInline) {
    if (phase === 'thanks') {
      return (
        <View style={styles.inlineBar}>
          <View style={styles.inlineThanksRow}>
            <MaterialCommunityIcons name="check-circle" size={16} color="#3AE869" />
            <Text style={styles.inlineThanksText}>Thanks for the feedback!</Text>
          </View>
        </View>
      );
    }
    // Mobile comment step: reached only after a thumbs down-vote (see
    // shouldComment). Stacks a prompt, the text box, and Send below the bar.
    if (phase === 'comment') {
      return (
        <View style={styles.inlineComment}>
          <View style={styles.inlineCommentHead}>
            <MaterialCommunityIcons name="thumb-down" size={14} color="#FF7B00" />
            <Text style={styles.inlineCommentPrompt} numberOfLines={1}>
              Thanks, noted. Anything to add?
            </Text>
            <Pressable
              onPress={handleDismiss}
              hitSlop={10}
              accessibilityRole="button"
              accessibilityLabel="Dismiss survey"
            >
              <MaterialCommunityIcons name="close" size={16} color="rgba(255,255,255,0.35)" />
            </Pressable>
          </View>
          <TextInput
            style={styles.input}
            placeholder={survey.message_placeholder}
            placeholderTextColor="rgba(255,255,255,0.4)"
            value={message}
            onChangeText={setMessage}
            multiline
            numberOfLines={3}
            maxLength={500}
            editable={!submitting}
          />
          <Pressable
            onPress={handleSend}
            disabled={submitting}
            style={[styles.ctaWrapper, submitting && styles.ctaDisabled]}
            accessibilityRole="button"
            accessibilityLabel="Send feedback"
          >
            <LinearGradient
              colors={['#538AF9', '#2466ED']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.cta}
            >
              <Text style={styles.ctaText}>{submitting ? 'Sending…' : 'Send'}</Text>
            </LinearGradient>
          </Pressable>
        </View>
      );
    }
    return (
      <View style={styles.inlineBar}>
        <Text style={styles.inlineQuestion} numberOfLines={1}>
          {survey.question}
        </Text>
        <SurveyQuestionInput
          type={survey.type}
          config={survey.config}
          value={answerValue}
          onChange={handlePick}
          disabled={submitting}
          compact
        />
        <Pressable
          onPress={handleDismiss}
          hitSlop={10}
          accessibilityRole="button"
          accessibilityLabel="Dismiss survey"
        >
          <MaterialCommunityIcons name="close" size={16} color="rgba(255,255,255,0.35)" />
        </Pressable>
      </View>
    );
  }
  return (
    <View style={styles.anchor} pointerEvents="box-none">
      {panel}
    </View>
  );
};

/**
 * Global, DESKTOP-ONLY wrapper. Mobile uses the inline `SurveyCard` mounted in
 * the feed / listing-detail screens, so this returns null on mobile.
 */
const SurveyFloatingCard = () => {
  const pathname = usePathname();
  const { isMobile } = useResponsive();
  const listingTourActive = useListingTourActive();

  const surface = surfaceForPath(pathname);
  const listingId =
    surface === 'listing_detail'
      ? decodeURIComponent((pathname || '').split('/')[2] || '')
      : null;
  const context = useMemo(
    () => (listingId ? { listing_id: listingId } : EMPTY_CONTEXT),
    [listingId]
  );

  if (isMobile || !surface || listingTourActive) return null;
  return <SurveyCard surface={surface} context={context} presentation="floating" />;
};

const styles = StyleSheet.create({
  // `position: 'fixed'` is web-only; Platform guard keeps native from receiving it.
  anchor: {
    ...(Platform.OS === 'web' ? { position: 'fixed' } : { position: 'absolute' }),
    bottom: 24,
    right: 24,
    zIndex: 210, // above DesktopProFloatingCard (200)
    width: 300,
  },
  // Inline (mobile): minimalist single-line bar below the header. No frame /
  // background — blends with the header strip it sits under.
  inlineBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  inlineQuestion: {
    flex: 1,
    fontFamily: 'Satoshi-Medium',
    fontSize: 13,
    color: 'rgba(255,255,255,0.85)',
  },
  // Mobile comment step: a small stacked block (prompt row + text box + Send),
  // shown in the same below-header slot the inline bar uses.
  inlineComment: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    gap: 0,
  },
  inlineCommentHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  inlineCommentPrompt: {
    flex: 1,
    fontFamily: 'Satoshi-Medium',
    fontSize: 13,
    color: 'rgba(255,255,255,0.85)',
  },
  inlineThanksRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  inlineThanksText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 13,
    color: 'rgba(255,255,255,0.85)',
  },
  card: {
    position: 'relative',
    padding: 16,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(83, 138, 249, 0.22)',
    backgroundColor: '#0F1729',
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.35,
    shadowRadius: 24,
    elevation: 10,
    ...(Platform.OS === 'web'
      ? {
          backgroundImage:
            'radial-gradient(200px 100px at 100% 0%, rgba(83,138,249,0.2), transparent 60%), linear-gradient(180deg, rgba(20,76,184,0.18) 0%, rgba(20,76,184,0.06) 100%)',
        }
      : {}),
  },
  closeBtn: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    alignItems: 'center',
    justifyContent: 'center',
    // The "×" glyph sits slightly low; nudge it up so it reads centered.
    paddingBottom: 3,
    zIndex: 2,
  },
  closeBtnText: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 16,
    lineHeight: 18,
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 11,
    letterSpacing: 1,
    color: '#8FB3FF',
    textTransform: 'uppercase',
  },
  title: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 15,
    lineHeight: 20,
    color: '#FFFFFF',
    marginTop: 8,
    paddingRight: 22, // clear the close button
  },
  chosenRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 12,
  },
  chosenText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 12.5,
    color: 'rgba(255,255,255,0.7)',
  },
  input: {
    marginTop: 10,
    minHeight: 64,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(255,255,255,0.03)',
    paddingHorizontal: 12,
    paddingTop: 10,
    paddingBottom: 10,
    color: '#FFFFFF',
    fontFamily: 'Satoshi-Regular',
    fontSize: 13,
    textAlignVertical: 'top',
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
  },
  ctaWrapper: {
    marginTop: 12,
  },
  ctaDisabled: {
    opacity: 0.6,
  },
  cta: {
    paddingVertical: 10,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: 'rgba(20,76,184,0.35)',
    shadowOffset: { width: 0, height: 5 },
    shadowOpacity: 1,
    shadowRadius: 14,
  },
  ctaText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 13,
    color: '#FFFFFF',
    letterSpacing: -0.1,
  },
  thanksWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 4,
  },
  thanksText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 14,
    color: '#FFFFFF',
  },
});

export default SurveyFloatingCard;
